import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Send, MessageSquare, Code2 } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import ItsmPayloadDialog from "@/components/ItsmPayloadDialog";
import ServiceNowPanel from "@/components/ServiceNowPanel";
import { formatDateTime, displayReqNumber } from "@/lib/format";
import { ExternalLink } from "lucide-react";

const STATUSES = ["Submitted", "Pending Approval", "In Progress", "Approved", "Fulfilled", "Rejected"];

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { userName } = usePersona();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [snOpen, setSnOpen] = useState(false);

  const load = () => {
    setLoading(true);
    endpoints.ticket(id).then((d) => { setTicket(d); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const post = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await endpoints.addComment(id, { author: userName || "Employee", role: "User", message: comment });
      setComment("");
      toast.success("Comment posted");
      load();
    } finally { setPosting(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await endpoints.updateStatus(id, { status: newStatus, note: `Status changed by ${userName}` });
      toast.success(`Ticket moved to ${newStatus}`);
      load();
    } catch { toast.error("Failed to update"); }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;
  }
  if (!ticket) {
    return <div className="text-center text-slate-500 py-16">Ticket not found. <Link to="/requests?view=all" className="text-[#0033A0] underline">Back to tickets</Link></div>;
  }

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <button onClick={() => nav(-1)} data-testid="ticket-back" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Ticket · ServiceNow</div>
          <h1 className="font-display font-black text-3xl text-slate-900 mt-1 tracking-tight">{ticket.subject}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span data-testid="ticket-detail-req" className="font-mono text-sm font-bold text-[#0033A0]">{displayReqNumber(ticket)}</span>
            {ticket.sn_number && ticket.sn_number !== ticket.req_number && (
              <span className="font-mono text-[10px] text-slate-400" title="Local Flexspace REQ">fx {ticket.req_number}</span>
            )}
            <StatusPill status={ticket.status} testId="ticket-detail-status" />
            <span className="text-xs text-slate-500">SLA: {ticket.sla_hours}h · {ticket.assigned_group}</span>
            {ticket.sn_sys_id && (
              <button
                data-testid="ticket-detail-open-sn"
                onClick={() => setSnOpen(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono bg-[#293e40] text-white border border-[#293e40] hover:bg-[#1a2828]"
                title="View live ServiceNow record"
              >
                ServiceNow · {ticket.sn_number}
                <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setPayloadOpen(true)} data-testid="view-itsm-payload">
            <Code2 size={14} className="mr-2 text-[#0033A0]" /> View ITSM Payload
          </Button>
          <div className="text-xs text-slate-500 ml-2">Change status</div>
          <Select value={ticket.status} onValueChange={changeStatus}>
            <SelectTrigger data-testid="status-select" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${s.replace(/\s+/g, '-')}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* Timeline */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <Clock size={16} className="text-[#0033A0]" /> Timeline
          </h2>
          <div className="mt-4 relative pl-6" data-testid="ticket-timeline">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
            {ticket.timeline.map((e, i) => (
              <div key={i} className="relative pb-5 last:pb-0">
                <div className={`absolute -left-6 top-1 w-4 h-4 rounded-full grid place-items-center ${i === ticket.timeline.length - 1 ? "bg-[#0033A0]" : "bg-white border-2 border-[#0033A0]"}`}>
                  {i === ticket.timeline.length - 1 && <CheckCircle2 size={10} className="text-white" />}
                </div>
                <div className="text-sm font-semibold text-slate-900">{e.status}</div>
                <div className="text-xs text-slate-500">{formatDateTime(e.timestamp)}</div>
                {e.note && <div className="text-xs text-slate-600 mt-1">{e.note}</div>}
              </div>
            ))}
          </div>
        </Card>

        {/* Comments */}
        <Card className="p-6 border-slate-200 shadow-sm flex flex-col">
          <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <MessageSquare size={16} className="text-[#0033A0]" /> Activity &amp; Comments
          </h2>
          <div className="mt-4 flex-1 space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="ticket-comments">
            {ticket.comments.map((c, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-slate-900">{c.author}</div>
                  <div className="text-[10px] text-slate-500">{formatDateTime(c.timestamp)}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{c.role}</div>
                <div className="text-sm text-slate-700 mt-1.5">{c.message}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-slate-200 pt-4">
            <Textarea
              data-testid="comment-input"
              placeholder="Add a note or update…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-20"
            />
            <Button
              data-testid="post-comment-btn"
              onClick={post}
              disabled={posting || !comment.trim()}
              className="mt-2 bg-[#0033A0] hover:bg-[#002573] text-white w-full"
            >
              <Send size={14} className="mr-2" /> Post Comment
            </Button>
          </div>
        </Card>
      </div>

      <ItsmPayloadDialog ticketId={id} open={payloadOpen} onOpenChange={setPayloadOpen} />

      <ServiceNowPanel
        open={snOpen}
        onOpenChange={setSnOpen}
        table={ticket.sn_table || (ticket.category === "Software Request" ? "sc_request" : "incident")}
        sysId={ticket.sn_sys_id || ""}
        number={ticket.sn_number || ""}
        fallbackReq={ticket.req_number || ""}
      />
    </div>
  );
}
